import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface SyncStatus {
  isRunning: boolean;
  lastRun: string | null;
  lastResults: {
    total: number;
    successful: number;
    failed: number;
    skipped: number;
  } | null;
  error: string | null;
  runCount: number;
  totalSynced: number;
  scheduledStatus: boolean; // Track if scheduled syncs are active
}

export function ExternalSyncControl() {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const { toast } = useToast();

  // Fetch the current status
  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/external-sync/status');
      if (!response.ok) {
        throw new Error('Failed to fetch sync status');
      }
      const data = await response.json();
      setStatus(data.status);
    } catch (error) {
      console.error('Error fetching sync status:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch sync status. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Start the sync process
  const startSync = async () => {
    setIsStarting(true);
    try {
      const response = await fetch('/api/external-sync/start', {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error('Failed to start sync process');
      }
      const data = await response.json();
      if (data.success) {
        toast({
          title: 'Sync Started',
          description: 'External sync process has been started successfully.',
        });
        // Update status immediately
        setStatus(data.status);
        // Then poll for updates
        setTimeout(fetchStatus, 2000);
      } else {
        toast({
          title: 'Warning',
          description: data.message || 'Unknown error starting sync process',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error starting sync:', error);
      toast({
        title: 'Error',
        description: 'Failed to start sync process. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsStarting(false);
    }
  };

  // Toggle scheduled sync status
  const toggleScheduledSync = async (enabled: boolean) => {
    try {
      const response = await fetch('/api/external-sync/schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: enabled }),
      });

      if (!response.ok) {
        throw new Error('Failed to update schedule status');
      }

      const data = await response.json();
      if (data.success) {
        setStatus(data.status);
        toast({
          title: enabled ? 'Schedule Enabled' : 'Schedule Disabled',
          description: enabled
            ? 'Automatic sync will run every 30 minutes from 8 AM to 6 PM.'
            : 'Automatic sync has been disabled.',
        });
      } else {
        toast({
          title: 'Warning',
          description: data.message || 'Unknown error updating schedule status',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error toggling scheduled sync:', error);
      toast({
        title: 'Error',
        description: 'Failed to update scheduling. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Initial load
  useEffect(() => {
    fetchStatus();

    // Poll for updates every 10 seconds
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          External Database Sync
          {status?.isRunning && (
            <Badge variant="outline" className="ml-2 bg-blue-100 text-blue-800">
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Syncing
            </Badge>
          )}
        </CardTitle>
        <CardDescription>Sync leads from the external landing page database</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !status ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
                <p className="text-lg font-semibold">{status?.isRunning ? 'Running' : 'Idle'}</p>
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-muted-foreground">Last Run</h3>
                <p className="text-lg font-semibold">
                  {status?.lastRun
                    ? formatDistanceToNow(new Date(status.lastRun), { addSuffix: true })
                    : 'Never'}
                </p>
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-muted-foreground">Total Leads Synced</h3>
                <p className="text-lg font-semibold">{status?.totalSynced || 0}</p>
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-muted-foreground">Run Count</h3>
                <p className="text-lg font-semibold">{status?.runCount || 0}</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between space-x-2">
                <div>
                  <h3 className="text-sm font-medium mb-1">Scheduled Sync</h3>
                  <p className="text-sm text-muted-foreground">
                    When enabled, sync will run automatically every 30 minutes from 8 AM to 6 PM
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="scheduled-sync"
                    checked={status?.scheduledStatus || false}
                    onCheckedChange={toggleScheduledSync}
                    disabled={loading}
                  />
                  <Label htmlFor="scheduled-sync">
                    {status?.scheduledStatus ? 'Enabled' : 'Disabled'}
                  </Label>
                </div>
              </div>
            </div>

            {status?.lastResults && (
              <div className="pt-4 border-t">
                <h3 className="text-sm font-medium mb-2">Last Run Results</h3>
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-slate-100 p-3 rounded text-center">
                    <p className="text-sm text-muted-foreground">Total</p>
                    <p className="text-lg font-semibold">{status.lastResults.total}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded text-center">
                    <p className="text-sm text-muted-foreground">Successful</p>
                    <p className="text-lg font-semibold text-green-700">
                      {status.lastResults.successful}
                    </p>
                  </div>
                  <div className="bg-red-100 p-3 rounded text-center">
                    <p className="text-sm text-muted-foreground">Failed</p>
                    <p className="text-lg font-semibold text-red-700">
                      {status.lastResults.failed}
                    </p>
                  </div>
                  <div className="bg-yellow-100 p-3 rounded text-center">
                    <p className="text-sm text-muted-foreground">Skipped</p>
                    <p className="text-lg font-semibold text-yellow-700">
                      {status.lastResults.skipped}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {status?.error && (
              <div className="pt-2 pb-2">
                <h3 className="text-sm font-medium text-red-700 mb-1">Error</h3>
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm overflow-auto max-h-24">
                  {status.error}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={fetchStatus} disabled={loading}>
          Refresh Status
        </Button>
        <Button onClick={startSync} disabled={isStarting || status?.isRunning}>
          {isStarting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            'Start Sync Now'
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
